import { TextModel } from '../primitive/text.model';
import { DropdownModel } from './dropdown.model';
import { DateModel } from '../primitive/date.model';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator, JsonDisplayDecorator } from '../../decorators/display-decorator';
import { CompositeModel, ODataCompositeOutModel } from './composite.model';
import moment from 'moment/moment';
import { MultilineTextModel } from '../primitive/multiline-text.model';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationIdentifierModel } from '../low-level/notification-identifier.model';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { BooleanModel } from '../primitive/boolean.model';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { DateModelOptions } from '../options/date-model-options';
import { TranslateService } from '@ngx-translate/core';
import { TextModelOptions } from '../options/text-model-options';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { SignaturePad } from '../../components/signature-pad/signature-pad';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { BooleanModelOptions } from '../options/boolean-model-options';
import { NotificationModel } from '../low-level/notification.model';
import { ArrayModel, ArrayModelFromJson, JsonArrayModel } from './array.model';
import { ArrayModelOptions } from '../options/array-model-options';
import { ComponentInformationFromJson, ComponentInformationModel } from './fda2579-component-info.model';
import { Fda2579FormModelOptions } from '../options/fda2579-form-options.model';
import { CseDopModel } from './cse-dop.model';
import { AddressModel } from './address.model';

export class Fda2579FormModel extends CompositeModel {
    // Equipment location Information
    public notificationAddress: CseDopModel;

    // Assembler Information
    public assemblerCompanyName: TextModel;
    public assemblerStreet: TextModel;
    public assemblerCity: TextModel;
    public assemblerState: TextModel;
    public assemblerZipCode: TextModel;
    public assemblerPhoneNumber: TextModel;

    // General Information
    public certifiedComponentCheckboxList: Array<DisplayDecorator<BooleanModel>>;
    public intendedUsesCheckboxList: Array<DisplayDecorator<BooleanModel>>;
    public xRaySystemDropdown: DropdownModel<TextModel>; // Single    Selection
    public masterControlRoomNumber: TextModel;
    public dateOfAssembly: DateModel;

    // Component Information
    public masterControl: DropdownModel<TextModel>; // Single Selection
    public controlManufacturer: TextModel;
    public controlModelNumber: TextModel;
    public controlSerialNumber: TextModel;
    public manufacturedDate: DateModel;
    public systemModelName: TextModel;
    public componentList: ArrayModel<ComponentInformationModel>;

    public signatureDate: DateModel;
    public serviceEngineerEmail: TextModel;
    public comments: MultilineTextModel;

    public otherCertifiedComponentDropdownList: OtherCertifiedComponentDropdownInterface[];
    public customerSignaturePad: SignaturePad;
    public signature: TextModel;
    public notificationIdentifier: NotificationIdentifierModel;
    public isFda2579FormValid = false;
    public remainingComponentEntries: number;
    public maxEntries = 5;

    public accordionOpenedStatus: AccordionOpenedStatus = {
        equipmentInfo: false,
        assemblerInfo: false,
        generalInfo: false,
        componentInfo: false,
        assemblerCertification: false,
        comments: false,
    };

    public constructor(
        notificationModel: NotificationModel,
        public options = new Fda2579FormModelOptions(),
    ) {
        super('Fda2579FormModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const valueHelperService = injector.get(ValueHelperService);
        const translate = injector.get(TranslateService);
        this.notificationAddress = CseDopModel.fromAddressModel(notificationModel.address);
        this.notificationAddress.setFieldValidations('Fda2579Form');
        AddressModel.updateStateOfUSA(this.notificationAddress);
        // Predefined data , it will never change, so disable it
        const optionsText = new TextModelOptions({ readonly: true });
        this.assemblerCompanyName = <TextModel>(
            this.set(new TextModel('_2a_company_name', 'Siemens Medical Solution USA. Inc.', optionsText))
        );
        this.assemblerStreet = <TextModel>this.set(new TextModel('_2b_address', '221 Gregson Drive', optionsText));
        this.assemblerCity = <TextModel>this.set(new TextModel('_2c_city', 'Cary', optionsText));
        this.assemblerState = <TextModel>this.set(new TextModel('_2d_state', 'NC', optionsText));
        this.assemblerZipCode = <TextModel>this.set(new TextModel('_2e_zip', '27511', optionsText));
        this.assemblerPhoneNumber = <TextModel>this.set(new TextModel('_2f_telephone', '(919)804 8395', optionsText));

        this.serviceEngineerEmail = <TextModel>this.set(new TextModel('_5a'));
        modelOptionsLoader.load(this.serviceEngineerEmail, 'Fda2579Form', 'ServiceEngineerEmail');

        const rowAmount = 5;
        this.comments = <MultilineTextModel>this.set(new MultilineTextModel('_6'));
        this.comments = assignModelOptions(
            this.comments,
            {
                ...this.comments.options,
                rows: rowAmount,
                placeholder: <string>translate.instant('FDA_2579_FORM_COMPONENT_COMMENT_PLACEHOLDER'),
            },
            this,
        );
        modelOptionsLoader.load(this.comments, 'Fda2579Form', 'Comments');
        const checkBoxOptions = new BooleanModelOptions({ isBinaryValue: true });
        this.certifiedComponentCheckboxList = valueHelperService
            .getFDAFormCertifiedComponent()
            .map((certifiedComponent) => {
                return <DisplayDecorator<BooleanModel>>(
                    this.set(
                        new DisplayDecorator(
                            certifiedComponent.descText,
                            new BooleanModel(certifiedComponent.code, false, checkBoxOptions),
                        ),
                    )
                );
            });
        this.intendedUsesCheckboxList = valueHelperService.getFDAFormIntendedUses().map((intendedUses) => {
            return <DisplayDecorator<BooleanModel>>(
                this.set(
                    new DisplayDecorator(
                        intendedUses.descText,
                        new BooleanModel(intendedUses.code, false, checkBoxOptions),
                    ),
                )
            );
        });
        const xraySystemList = valueHelperService.getXRaySystems().map((vh: ValueHelperModel) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.descText, vh.code));
        });
        this.xRaySystemDropdown = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('_3c', xraySystemList))
        );
        modelOptionsLoader.load(this.xRaySystemDropdown, 'Fda2579Form', 'XRaySystem');

        this.masterControlRoomNumber = <TextModel>this.set(new TextModel('_3d_master'));
        modelOptionsLoader.load(this.masterControlRoomNumber, 'Fda2579Form', 'ControlRoomNumber');

        const now = moment().startOf('days');
        const dateOption = new DateModelOptions({ format: ConfigConstants.formatPatternDateFDA });
        this.dateOfAssembly = <DateModel>this.set(new DateModel('_3e_month', now, dateOption));
        modelOptionsLoader.load(this.dateOfAssembly, 'Fda2579Form', 'DateOfAssembly');

        const masterControlOptions: Array<DisplayDecorator<TextModel>> = [];
        masterControlOptions.push(new DisplayDecorator('A New installation', new TextModel('New installation', '0')));
        masterControlOptions.push(
            new DisplayDecorator('Existing (Certified)', new TextModel('Existing (Certified)', '1')),
        );
        masterControlOptions.push(
            new DisplayDecorator('Existing (Non Certified)', new TextModel('Existing (Non Certified)', '2')),
        );
        this.masterControl = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('_4a1', masterControlOptions))
        );
        modelOptionsLoader.load(this.masterControl, 'Fda2579Form', 'MasterControl');
        this.controlManufacturer = <TextModel>this.set(new TextModel('_4b', 'Siemens AG'));
        modelOptionsLoader.load(this.controlManufacturer, 'Fda2579Form', 'Manufacturer');
        this.controlModelNumber = <TextModel>this.set(new TextModel('_4c'));
        modelOptionsLoader.load(this.controlModelNumber, 'Fda2579Form', 'ControlModelNumber');
        this.controlSerialNumber = <TextModel>this.set(new TextModel('_4d'));
        modelOptionsLoader.load(this.controlSerialNumber, 'Fda2579Form', 'ControlSerialNumber');
        this.systemModelName = <TextModel>this.set(new TextModel('_4f'));
        modelOptionsLoader.load(this.systemModelName, 'Fda2579Form', 'SystemModelName');
        const dateModelOption = new DateModelOptions({
            format: ConfigConstants.formatPatternMonthYear,
            displayOnlyMonthYear: true,
        });
        this.manufacturedDate = <DateModel>this.set(new DateModel('_4e', moment.invalid(), dateModelOption));
        modelOptionsLoader.load(this.manufacturedDate, 'Fda2579Form', 'OperationDate');

        this.componentList = new ArrayModel<ComponentInformationModel>(
            'Fda2579Form',
            [],
            new ArrayModelOptions({ maxEntries: this.maxEntries }),
        );

        this.signatureDate = <DateModel>this.set(new DateModel('_5c', now, dateOption));
        this.signature = <TextModel>this.set(new TextModel('_5b'));
        modelOptionsLoader.load(this.signature, 'Fda2579Form', 'Signature');

        const optionGroup: Array<DisplayDecorator<TextModel>> = [];
        optionGroup.push(
            new DisplayDecorator(
                ConfigConstants.FDACertifiedOption0,
                new TextModel(ConfigConstants.FDACertifiedOption0, ConfigConstants.FDACertifiedOptionValue0),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                ConfigConstants.FDACertifiedOption1,
                new TextModel(ConfigConstants.FDACertifiedOption1, ConfigConstants.FDACertifiedOptionValue1),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                ConfigConstants.FDACertifiedOption2,
                new TextModel(ConfigConstants.FDACertifiedOption2, ConfigConstants.FDACertifiedOptionValue2),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                ConfigConstants.FDACertifiedOption3,
                new TextModel(ConfigConstants.FDACertifiedOption3, ConfigConstants.FDACertifiedOptionValue3),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                ConfigConstants.FDACertifiedOption4,
                new TextModel(ConfigConstants.FDACertifiedOption4, ConfigConstants.FDACertifiedOptionValue4),
            ),
        );
        // There are 15+ "Other Certified" components, each with a dropdown having fixed options [0,1,2,3,4].
        // These options are static and do not come from the gateway.
        // Each component has:
        //   - entry.code (e.g. _4h01, _4h02 ... _4h15) → used as the key to send to backend in getOutModel()
        //   - entry.descText (e.g. "High voltage generator") → used as the label in UI
        // The dropdownOutModel stores values by dropdown "name" (i.e. outModel[this.name]).
        // Since we cannot directly show the code (_4h01, _4h02...) in the UI,
        // we use an interface `OtherCertifiedComponentDropdownInterface` with { label, dropdown }.
        // Using *ngFor, we loop through this interface to dynamically create the dropdowns with labels.

        this.otherCertifiedComponentDropdownList = valueHelperService
            .getFDAFormOtherCertifiedComponent()
            .map((entry) => {
                const label = entry.descText;
                const dropdownModel = <DropdownModel<TextModel>>(
                    this.set(new DropdownModel<TextModel>(entry.code, optionGroup))
                );
                modelOptionsLoader.load(dropdownModel, 'Fda2579Form', 'OtherCertifiedComponent');
                dropdownModel.selectByIndex(0);

                return { label, dropdownModel, exceedsMaxValue: false, maxValue: 4 };
            });
        this.addComponentInformationModel();
    }

    /**
     * Updates the maximum allowed entries for each certified component dropdown.
     *
     * Logic:
     * - The total number of components allowed is 5.
     * - Calculates how many components are already selected across all dropdowns.
     * - Based on the remaining count, updates each dropdown’s `maxValue` and marks entries as invalid when exceeded.
     * - If a user selects a higher value (e.g., selects 4), the system automatically ensures that
     *   the required number of component information models are added.
     *
     * Example:
     *   If 2 components are already added, the user can select up to 3 more.
     *   If a dropdown has value 4 selected, then 4 component info entries must exist.
     */
    public updateComponentListMaxEntries(): void {
        let totalUsed = 0;
        for (const item of this.otherCertifiedComponentDropdownList) {
            totalUsed += parseInt(item.dropdownModel.selectedEntry?.model?.value, 10);
        }
        this.remainingComponentEntries = this.maxEntries - totalUsed;

        this.otherCertifiedComponentDropdownList.forEach((item) => {
            const currentSelectedValue = parseInt(item.dropdownModel.selectedEntry.model.value, 10);
            const remainingEntries = totalUsed - currentSelectedValue;
            item.maxValue = Math.max(0, this.maxEntries - remainingEntries);
            item.exceedsMaxValue = currentSelectedValue > item.maxValue;

            item.dropdownModel = assignModelOptions(
                item.dropdownModel,
                {
                    ...item.dropdownModel.options,
                    invalidSelection: item.exceedsMaxValue,
                },
                this,
            );
        });

        // Example : If value 4 is selected in dropdown, then it is mandatory to add 4 Component information
        if (totalUsed > this.componentList.value.length) {
            const minModelsToBeAdded = totalUsed - this.componentList.value.length;
            for (let i = 0; i < minModelsToBeAdded; i++) {
                this.addComponentInformationModel();
            }
        }
    }

    public updateMaxValueForOtherCertifiedComponent(): void {
        const maxEntries = this.componentList.options.maxEntries;
        const maxValue = maxEntries - this.componentList.value.length;
        this.otherCertifiedComponentDropdownList.forEach((item) => {
            item.maxValue = maxValue;
        });
    }

    public addComponentInformationModel(): void {
        this.componentList.addModel(new ComponentInformationModel());
        this.updateMaxValueForOtherCertifiedComponent();
    }

    public removeComponentInformationModel(index: number): void {
        this.componentList.removeModel(index);
        this.updateMaxValueForOtherCertifiedComponent();
    }

    public getOutModel(): ODataCompositeOutModel {
        const locationNameKey = '_1a_hosp_name';
        const locationCityKey = '_1c_city';
        const locationStreetKey = '_1b_address';
        const locationStateDropdownKey = '_1d_state';
        const locationZipCodeKey = '_1e_zip';
        const locationPhoneNumber = '_1f_telephone';
        const signatureKey = '_5g';
        const commentKey = '_6';
        const outModel = <FdaFormOutModel>super.getOutModel();

        // Since standard multilineTextModel outModel sends data in Array example (with Counter: "", TextLine:),
        // but USA backend expect us to send the entire text as simple string.
        outModel[commentKey] = this.comments.value;
        outModel[signatureKey] = this.customerSignaturePad.dataUrl;

        outModel[locationNameKey] = this.notificationAddress.name1.value;
        outModel[locationCityKey] = this.notificationAddress.city.value;
        outModel[locationStreetKey] = this.notificationAddress.street.value;
        outModel[locationStateDropdownKey] = this.notificationAddress.statesOfUSA.selectedEntry?.model.value;
        outModel[locationZipCodeKey] = this.notificationAddress.postalCode.value;
        outModel[locationPhoneNumber] = this.notificationAddress.phone.value;

        return outModel;
    }

    public toJSON(): unknown {
        const parsedSignature = EncodingUtilities.base64EncodeUnicode(this.signature.value);
        return {
            notificationIdentifierModel: this.notificationIdentifier,
            // Equipment Location Information
            locationName: this.notificationAddress.name1.value,
            locationStreet: this.notificationAddress.street.value,
            locationCity: this.notificationAddress.city.value,
            locationState: this.notificationAddress.statesOfUSA.selectedEntry?.model.value,
            locationZipCode: this.notificationAddress.postalCode.value,
            locationPhoneNumber: this.notificationAddress.phone.value,

            // Assembler Information
            assemblerCompanyName: this.assemblerCompanyName.value,
            assemblerStreet: this.assemblerStreet.value,
            assemblerCity: this.assemblerCity.value,
            assemblerState: this.assemblerState.value,
            assemblerZipCode: this.assemblerZipCode.value,
            assemblerPhoneNumber: this.assemblerPhoneNumber.value,

            // General Information
            certifiedComponentCheckboxList: this.certifiedComponentCheckboxList,
            intendedUsesCheckboxList: this.intendedUsesCheckboxList,
            xRaySystemDropdown: this.xRaySystemDropdown.selectedEntry?.value,
            masterControlRoomNumber: this.masterControlRoomNumber.value,
            dateOfAssembly: this.dateOfAssembly.value,

            // Component Information
            masterControl: this.masterControl.selectedEntry?.value,
            controlManufacturer: this.controlManufacturer.value,
            controlModelNumber: this.controlModelNumber.value,
            controlSerialNumber: this.controlSerialNumber.value,
            manufacturedDate: this.manufacturedDate.value,
            systemModelName: this.systemModelName.value,

            otherCertifiedComponentDropdownList: this.otherCertifiedComponentDropdownList.map((item) => ({
                label: item.label,
                dropdownValue: item.dropdownModel.selectedEntry?.model.value,
            })),
            componentList: this.componentList,

            // Misc
            signatureDate: this.signatureDate.value,
            serviceEngineerEmail: this.serviceEngineerEmail.value,
            comments: this.comments.value,

            // Signature
            customerSignature: parsedSignature,
            accordionOpenedStatus: this.accordionOpenedStatus,
            isFdaFormValid: this.isFda2579FormValid,
        };
    }
}

export class Fda2579FormModelFromJson extends Fda2579FormModel {
    public constructor(data: JsonFda2579FormModel, notification: NotificationModel) {
        super(notification);
        this.isFda2579FormValid = data.isFda2579FormValid;
        // Location
        this.notificationAddress.name1.value = data.locationName;
        this.notificationAddress.street.value = data.locationStreet;
        this.notificationAddress.city.value = data.locationCity;
        this.notificationAddress.statesOfUSA?.selectByPredicate((entry) => entry.model.value === data.locationState);
        this.notificationAddress.postalCode.value = data.locationZipCode;
        this.notificationAddress.phone.value = data.locationPhoneNumber;

        // Assembler
        this.assemblerCompanyName.value = data.assemblerCompanyName;
        this.assemblerStreet.value = data.assemblerStreet;
        this.assemblerCity.value = data.assemblerCity;
        this.assemblerState.value = data.assemblerState;
        this.assemblerZipCode.value = data.assemblerZipCode;
        this.assemblerPhoneNumber.value = data.assemblerPhoneNumber;

        // Dropdowns & general info
        data.certifiedComponentCheckboxList.forEach((item) =>
            this.certifiedComponentCheckboxList.forEach((certifiedComponent: DisplayDecorator<BooleanModel>) => {
                if (item.model.name === certifiedComponent.model.name) {
                    certifiedComponent.model.value = <boolean>item.model.value;
                }
            }),
        );
        data.intendedUsesCheckboxList.forEach((item) =>
            this.intendedUsesCheckboxList.forEach((intendedUses: DisplayDecorator<BooleanModel>) => {
                if (item.model.name === intendedUses.model.name) {
                    intendedUses.model.value = <boolean>item.model.value;
                }
            }),
        );
        this.xRaySystemDropdown.selectByPredicate((item) => item.model.value === data.xRaySystemDropdown);
        this.masterControlRoomNumber.value = data.masterControlRoomNumber;
        this.dateOfAssembly.value = moment(data.dateOfAssembly);

        // Master Control
        this.masterControl.selectByPredicate((item) => item.model.value === data.masterControl);
        this.controlManufacturer.value = data.controlManufacturer;
        this.controlModelNumber.value = data.controlModelNumber;
        this.controlSerialNumber.value = data.controlSerialNumber;
        this.manufacturedDate.value = moment(data.manufacturedDate);
        this.systemModelName.value = data.systemModelName;

        data.otherCertifiedComponentDropdownList.forEach((jsonComponent) => {
            this.otherCertifiedComponentDropdownList.forEach((component) => {
                if (jsonComponent.label === component.label) {
                    component.dropdownModel.selectByValue(jsonComponent.dropdownValue);
                }
            });
        });
        if (data.componentList) {
            this.componentList = new ArrayModelFromJson(ComponentInformationFromJson, data.componentList);
        }
        // Signature & misc
        this.signatureDate.value = moment(data.signatureDate);
        this.serviceEngineerEmail.value = data.serviceEngineerEmail;
        this.comments.value = data.comments;
        this.signature.value = EncodingUtilities.base64DecodeUnicode(data.customerSignature);

        // Saving Accordion status
        this.accordionOpenedStatus.equipmentInfo = data.accordionOpenedStatus?.equipmentInfo ?? false;
        this.accordionOpenedStatus.assemblerInfo = data.accordionOpenedStatus?.assemblerInfo ?? false;
        this.accordionOpenedStatus.generalInfo = data.accordionOpenedStatus?.generalInfo ?? false;
        this.accordionOpenedStatus.componentInfo = data.accordionOpenedStatus?.componentInfo ?? false;
        this.accordionOpenedStatus.assemblerCertification = data.accordionOpenedStatus?.assemblerCertification ?? false;
        this.accordionOpenedStatus.comments = data.accordionOpenedStatus?.comments ?? false;
    }
}

export interface JsonFda2579FormModel {
    locationName: string;
    locationStreet: string;
    locationCity: string;
    locationState: string;
    locationZipCode: string;
    locationPhoneNumber: string;

    assemblerCompanyName: string;
    assemblerStreet: string;
    assemblerCity: string;
    assemblerState: string;
    assemblerZipCode: string;
    assemblerPhoneNumber: string;

    certifiedComponentCheckboxList: JsonDisplayDecorator[];
    intendedUsesCheckboxList: JsonDisplayDecorator[];
    xRaySystemDropdown: string;
    masterControlRoomNumber: string;
    dateOfAssembly: moment.Moment;

    masterControl: string;
    controlManufacturer: string;
    controlModelNumber: string;
    controlSerialNumber: string;
    manufacturedDate: moment.Moment;
    systemModelName: string;

    signatureDate: moment.Moment;
    serviceEngineerEmail: string;
    comments: string;

    accordionOpenedStatus: {
        equipmentInfo: boolean;
        assemblerInfo: boolean;
        generalInfo: boolean;
        componentInfo: boolean;
        assemblerCertification: boolean;
        comments: boolean;
    };
    customerSignature: string;
    isFda2579FormValid: boolean;
    otherCertifiedComponentDropdownList: JsonOtherCertifiedComponentDropdown[];
    componentList: JsonArrayModel;
}

interface AccordionOpenedStatus {
    [key: string]: boolean; // Index signature
    equipmentInfo: boolean;
    assemblerInfo: boolean;
    generalInfo: boolean;
    componentInfo: boolean;
    assemblerCertification: boolean;
    comments: boolean;
}

export interface OtherCertifiedComponentDropdownInterface {
    label: string;
    dropdownModel: DropdownModel<TextModel>;
    exceedsMaxValue: boolean;
    maxValue: number;
}

export interface JsonOtherCertifiedComponentDropdown {
    label: string;
    dropdownValue: string;
}

export interface FdaFormOutModel extends ODataObject {
    _1a_hosp_name: string;
    _1c_city: string;
    _1b_address: string;
    _1d_state: string;
    _1e_zip: string;
    _1f_telephone: string;

    _2a_company_name: string;
    _2b_address: string;
    _2c_city: string;
    _2d_state: string;
    _2e_zip: string;
    _2f_telephone: string;

    _3a1: string;
    _3a2: string;
    _3a3: string;
    _3a4: string;
    _3a5: string;

    _3b01: string;
    _3b02: string;
    _3b03: string;
    _3b04: string;
    _3b05: string;
    _3b06: string;
    _3b07: string;
    _3b08: string;
    _3b09: string;
    _3b10: string;
    _3b11: string;
    _3b12: string;
    _3b13: string;
    _3b14: string;
    _3b15: string;
    _3b16: string;
    _3b17: string;
    _3b18: string;
    _3b19: string;
    _3b20: string;
    _3b21: string;

    _3c: string;
    _3d_master: string;
    _3e_month: string;

    _4a1: string;
    _4b: string;
    _4c: string;
    _4d: string;
    _4e: string;
    _4f: string;

    _4g01: string;
    _4g02: string;
    _4g03: string;
    _4g04: string;
    _4g05: string;
    _4g06: string;
    _4g07: string;
    _4g08: string;
    _4g09: string;
    _4g10: string;
    _4g11: string;
    _4g12: string;
    _4g13: string;
    _4g14: string;
    _4g15: string;

    _4h01: string;
    _4h02: string;
    _4h03: string;
    _4h04: string;
    _4h05: string;
    _4h06: string;
    _4h07: string;
    _4h08: string;
    _4h09: string;
    _4h10: string;
    _4h11: string;
    _4h12: string;
    _4h13: string;
    _4h14: string;
    _4h15: string;

    _5a: string;
    _5b: string;
    _5c: string;

    _6: string;
}
